import React, { useContext } from 'react'

import { ItemCardVariant } from '../ItemCard'
import ItineraryItemCard from '../ItineraryItemCard'
import { PlannedItemCtaContent } from '../PlannedItemCtaContent'

import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { isLEOffer } from 'lib/offerTypes'
import { scheduleIsCurrent } from 'lib/scheduleStatus'
import LEOfferPriceDetails from 'tripPlanner/components/ItemOfferPrice/LEOfferPriceDetails'
import SavedOfferCard from 'tripPlanner/components/SavedOffers/SavedOfferCard'
import TripContext from 'tripPlanner/contexts/TripContext'
import useTourV1ItemConfirmationCodes from 'tripPlanner/hooks/confirmationCodes/useTourV1ItemConfirmationCodes'
import useTourV1ItemOccupancyDescription from 'tripPlanner/hooks/occupancy/useTourV1ItemOccupancyDescription'
import { TourItem } from 'tripPlanner/types/tripItem'
import {
  buildMyEscapesOrderLink,
  formatDatesAndDays,
  formatDurationDaysIfMultipleDays,
  formatStartAndEndLocation,
  isLeBookmark,
} from 'tripPlanner/utils'
import { buildProductLink } from 'tripPlanner/utils/bookmark/deepLink'

interface Props {
  item: TourItem
  offer: App.Offer
  onClickCard: (offerLink: string) => void
  onClickTripName?: () => void
  variant: ItemCardVariant
}

export default function TourV1ItemCard({
  item,
  offer,
  onClickCard,
  onClickTripName,
  variant,
}: Props) {
  const timeFormat = useRegionTimeFormat()

  const tripContext = useContext(TripContext)
  const viewAsTemplate = tripContext?.viewAsTemplate
  const canEdit = tripContext?.permissions.canEdit ?? false

  let priceAreaContent: JSX.Element | null = null
  let ctaAreaContent: JSX.Element | null = null
  let timeDescription: string | undefined = formatDatesAndDays(item, timeFormat)

  const isOfferAvailable = isLEOffer(offer)
    ? scheduleIsCurrent(offer.onlinePurchaseSchedule)
    : true

  const bookingId = useTourV1ItemConfirmationCodes(item)
  const productTypeLabel = offer ? (
    <ProductTypeLabel productType={offer.productType} />
  ) : null
  const durationDescription = formatDurationDaysIfMultipleDays(item)
  const travellersDescription = useTourV1ItemOccupancyDescription(item)

  if (isLeBookmark(item)) {
    timeDescription = undefined

    priceAreaContent = (
      <LEOfferPriceDetails
        offer={offer}
        package={offer.packages.find(
          (p) => p.id === item.savedItemData.packageId,
        )}
      />
    )
    ctaAreaContent = (
      <PlannedItemCtaContent
        item={item}
        isOfferAvailable={isOfferAvailable}
        offerId={item.code}
        checkIn={item.startDate}
        checkOut={item.endDate}
        variant={variant}
      />
    )

    const locationDescription = (
      <BodyText variant="small" lineClamp={2}>
        {item.startPlace.name}
      </BodyText>
    )

    const tileLink = buildProductLink(item, !viewAsTemplate, { offer }) ?? ''

    return (
      <SavedOfferCard
        item={item}
        name={item.name}
        imageOverlayContent={productTypeLabel}
        locationDescription={locationDescription}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        priceAreaContent={priceAreaContent}
        ctaAreaContent={ctaAreaContent}
        isSoldOut={!isOfferAvailable}
        onClick={() => onClickCard(tileLink)}
        testId="saved-tour-card"
        onClickTripName={onClickTripName}
        variant={variant}
      />
    )
  } else {
    const myEscapesLink =
      canEdit && !viewAsTemplate ? buildMyEscapesOrderLink(item) : undefined
    return (
      <ItineraryItemCard
        item={item}
        name={item.name}
        imageOverlayContent={productTypeLabel}
        locationDescription={formatStartAndEndLocation(item)}
        descriptors={[timeDescription, durationDescription]}
        printDescriptors={[
          timeDescription,
          durationDescription,
          travellersDescription,
        ]}
        confirmationCode={bookingId.codeString}
        confirmationCodeLabel={bookingId.label}
        onClickTripName={onClickTripName}
        variant={variant}
        onClick={myEscapesLink ? () => onClickCard(myEscapesLink) : undefined}
      />
    )
  }
}
